import React, { useCallback, useState, useEffect } from 'react';
import { NodeSelect, NodeTextField } from '../NodeUI';
import PeoplePicker from '../common/PeoplePicker';
import './PublicAccessPolicyEditor.css';

const ACCESS_MODES = [
  { value: 'DISABLED', label: 'Disabled' },
  { value: 'PUBLIC', label: 'Public' },
  { value: 'EMAIL_MAGIC_LINK', label: 'Email Magic Link' },
  { value: 'AUTHENTICATED_USERS', label: 'Authenticated Users' },
  { value: 'AUTHENTICATED_SELECTED_USERS', label: 'Authenticated Selected Users' },
];

const MODE_DESCRIPTIONS = {
  DISABLED: 'Access is disabled. No public/share-link access is allowed.',
  PUBLIC: 'Anyone with the link can access using a stable anonymous identity.',
  EMAIL_MAGIC_LINK: 'Users must verify their email via a magic link before accessing.',
  AUTHENTICATED_USERS: 'Only signed-in users can access.',
  AUTHENTICATED_SELECTED_USERS: 'Only specific signed-in users can access.',
};

const EMAIL_RESTRICTION_OPTIONS = [
  { value: 'ANY', label: 'Any email' },
  { value: 'ALLOWLIST', label: 'Specific emails only' },
  { value: 'DOMAIN', label: 'Specific domains only' },
];

const EMAIL_RESTRICTION_DESCRIPTIONS = {
  ANY: 'Any valid email address can be verified.',
  ALLOWLIST: 'Only listed email addresses can be verified.',
  DOMAIN: 'Only email addresses from listed domains can be verified.',
};

export default function PublicAccessPolicyEditor({
  accessPolicy,
  onChange,
  users = [],
  usersLoading = false,
  disabled = false,
  modes = ACCESS_MODES,
}) {
  const policy = accessPolicy || { mode: 'PUBLIC', selectedUserIds: [] };
  const mode = policy.mode || 'PUBLIC';
  const selectedUserIds = Array.isArray(policy.selectedUserIds) ? policy.selectedUserIds : [];
  const emailRestriction = policy.emailRestriction || 'ANY';
  const allowedEmails = Array.isArray(policy.allowedEmails) ? policy.allowedEmails : [];
  const allowedDomains = Array.isArray(policy.allowedDomains) ? policy.allowedDomains : [];

  const emit = useCallback(
    (patch) => onChange({ ...policy, ...patch }),
    [policy, onChange],
  );

  const handleModeChange = (newMode) => {
    emit({
      mode: newMode,
      selectedUserIds: newMode === 'AUTHENTICATED_SELECTED_USERS' ? selectedUserIds : [],
      emailRestriction: newMode === 'EMAIL_MAGIC_LINK' ? emailRestriction : 'ANY',
      allowedEmails: newMode === 'EMAIL_MAGIC_LINK' ? allowedEmails : [],
      allowedDomains: newMode === 'EMAIL_MAGIC_LINK' ? allowedDomains : [],
    });
  };

  const handleUserToggle = (userId) => {
    const next = selectedUserIds.includes(userId)
      ? selectedUserIds.filter((id) => id !== userId)
      : [...selectedUserIds, userId];
    emit({ selectedUserIds: next });
  };

  const handleEmailRestrictionChange = (value) => {
    emit({
      emailRestriction: value,
      allowedEmails: value === 'ALLOWLIST' ? allowedEmails : [],
      allowedDomains: value === 'DOMAIN' ? allowedDomains : [],
    });
  };

  const parseLines = (text) =>
    text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

  const [emailsText, setEmailsText] = useState(() => allowedEmails.join('\n'));
  const [domainsText, setDomainsText] = useState(() => allowedDomains.join('\n'));

  useEffect(() => { setEmailsText(allowedEmails.join('\n')); }, [allowedEmails.join('\n')]);
  useEffect(() => { setDomainsText(allowedDomains.join('\n')); }, [allowedDomains.join('\n')]);

  return (
    <div className="pap-root">
      <NodeSelect
        label="Access mode"
        value={mode}
        onChange={handleModeChange}
        options={modes}
        disabled={disabled}
      />
      {MODE_DESCRIPTIONS[mode] && (
        <p className="pap-hint">{MODE_DESCRIPTIONS[mode]}</p>
      )}

      {mode === 'EMAIL_MAGIC_LINK' && (
        <div className="pap-section">
          <NodeSelect
            label="Email restriction"
            value={emailRestriction}
            onChange={handleEmailRestrictionChange}
            options={EMAIL_RESTRICTION_OPTIONS}
            disabled={disabled}
          />
          <p className="pap-hint">{EMAIL_RESTRICTION_DESCRIPTIONS[emailRestriction]}</p>

          {emailRestriction === 'ALLOWLIST' && (
            <div className="pap-section">
              <NodeTextField
                label="Allowed emails"
                value={emailsText}
                onChange={setEmailsText}
                onBlur={() => emit({ allowedEmails: parseLines(emailsText) })}
                placeholder={'user@example.com\nanother@example.com'}
                multiline
                minRows={2}
                maxRows={6}
                disabled={disabled}
              />
              <p className="pap-hint">One email per line or comma-separated.</p>
              {allowedEmails.length === 0 && (
                <p className="pap-error">At least one email is required.</p>
              )}
            </div>
          )}

          {emailRestriction === 'DOMAIN' && (
            <div className="pap-section">
              <NodeTextField
                label="Allowed domains"
                value={domainsText}
                onChange={setDomainsText}
                onBlur={() => emit({ allowedDomains: parseLines(domainsText) })}
                placeholder={'acme.com\nexample.org'}
                multiline
                minRows={2}
                maxRows={6}
                disabled={disabled}
              />
              <p className="pap-hint">One domain per line or comma-separated.</p>
              {allowedDomains.length === 0 && (
                <p className="pap-error">At least one domain is required.</p>
              )}
            </div>
          )}
        </div>
      )}

      {mode === 'AUTHENTICATED_SELECTED_USERS' && (
        <div className="pap-section">
          <PeoplePicker
            items={users}
            selectedIds={selectedUserIds}
            onToggle={handleUserToggle}
            getId={(u) => u.userName}
            getName={(u) => u.displayName || u.userName}
            getDetail={(u) => u.email || u.userName}
            getAvatarLabel={(u) => u.displayName || u.userName || 'User'}
            loading={usersLoading}
            disabled={disabled}
            placeholder="Search for users..."
            emptyStateLabel="No users available"
          />
          {selectedUserIds.length === 0 && (
            <p className="pap-error">At least one user must be selected for this access mode.</p>
          )}
        </div>
      )}
    </div>
  );
}
